import type { ModelMeta } from '@/types'

import { MAX_MODEL_MB } from '@/constants/model3d'

// Browser-side helpers for the admin dashboard.

export class AdminRequestError extends Error {
    status: number

    constructor(message: string, status: number) {
        super(message)
        this.name = 'AdminRequestError'
        this.status = status
    }
}

// Every dashboard change goes through here: JSON, the X-EMA-Admin header the API requires,
// and a readable Indonesian error message when something fails.
export const adminFetch = async <T = Record<string, unknown>>(url: string, options: { method?: string, body?: unknown } = {}): Promise<T> => {
    let response: Response

    try {
        response = await fetch(url, {
            method: options.method ?? 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-EMA-Admin': '1'
            },
            body: options.body === undefined ? undefined : JSON.stringify(options.body)
        })
    }
    catch {
        throw new AdminRequestError('Koneksi terputus. Periksa internet lalu coba lagi.', 0)
    }

    const payload = await response.json().catch(() => null) as (T & { message?: string }) | null

    if (!response.ok) {
        // An expired session sends the admin back to the login page (but never from the login form itself).
        if (response.status === 401 && !url.startsWith('/api/admin/auth') && typeof window !== 'undefined') window.location.assign('/admin')

        throw new AdminRequestError(payload?.message ?? 'Terjadi kesalahan. Coba lagi.', response.status)
    }

    return (payload ?? {}) as T
}

const loadBitmap = async (file: File): Promise<{ source: CanvasImageSource, width: number, height: number, close: () => void }> => {
    if ('createImageBitmap' in window) {
        try {
            const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })

            return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close() }
        }
        catch {
            // Fall through to the <img> path, which some browsers decode more formats with.
        }
    }

    const url = URL.createObjectURL(file)

    try {
        const image = await new Promise<HTMLImageElement>((resolve, reject) => {
            const element = new Image()

            element.onload = () => resolve(element)
            element.onerror = () => reject(new Error('decode'))
            element.src = url
        })

        return { source: image, width: image.naturalWidth, height: image.naturalHeight, close: () => URL.revokeObjectURL(url) }
    }
    catch {
        URL.revokeObjectURL(url)
        throw new Error('Foto tidak bisa dibaca. Gunakan file JPG, PNG, atau WebP.')
    }
}

const toBlob = (canvas: HTMLCanvasElement, type: string, quality: number) => new Promise<Blob | null>(resolve => canvas.toBlob(resolve, type, quality))

const toBase64 = (blob: Blob) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]+,/, ''))
    reader.onerror = () => reject(new Error('Foto tidak bisa dibaca.'))
    reader.readAsDataURL(blob)
})

// Shrinks a photo to at most `maxSize` pixels on its longest side and re-encodes it (WebP when the
// browser can, JPEG otherwise), so the shop stays fast even with photos straight from a phone.
export const compressImage = async (file: File, options: { maxSize?: number, quality?: number, keepAlpha?: boolean } = {}) => {
    if (file.size > 30 * 1024 * 1024) throw new Error('Ukuran file terlalu besar. Maksimal 30 MB sebelum dikompres.')

    const maxSize = options.maxSize ?? 1600
    const quality = options.quality ?? 0.82
    const bitmap = await loadBitmap(file)

    try {
        const scale = Math.min(1, maxSize / Math.max(bitmap.width, bitmap.height))
        const width = Math.max(1, Math.round(bitmap.width * scale))
        const height = Math.max(1, Math.round(bitmap.height * scale))
        const canvas = document.createElement('canvas')

        canvas.width = width
        canvas.height = height

        const context = canvas.getContext('2d')

        if (!context) throw new Error('Browser ini tidak bisa memproses foto.')

        if (!options.keepAlpha) {
            context.fillStyle = '#ffffff'
            context.fillRect(0, 0, width, height)
        }

        context.imageSmoothingQuality = 'high'
        context.drawImage(bitmap.source, 0, 0, width, height)

        let blob = await toBlob(canvas, 'image/webp', quality)

        if (!blob || blob.type !== 'image/webp') blob = await toBlob(canvas, options.keepAlpha ? 'image/png' : 'image/jpeg', quality)
        if (!blob) throw new Error('Foto tidak bisa dikompres.')

        return { blob, type: blob.type, width, height }
    }
    finally {
        bitmap.close()
    }
}

export const uploadImage = async (file: File, options: { folder?: 'produk' | 'brand' | 'model-3d', name?: string, maxSize?: number, quality?: number, keepAlpha?: boolean } = {}): Promise<string> => {
    const compressed = await compressImage(file, { maxSize: options.maxSize, quality: options.quality, keepAlpha: options.keepAlpha })

    if (compressed.blob.size > 5 * 1024 * 1024) throw new Error('Foto masih terlalu besar setelah dikompres. Coba foto lain.')

    const data = await toBase64(compressed.blob)
    const result = await adminFetch<{ url: string }>('/api/admin/upload', {
        method: 'POST',
        body: { data, contentType: compressed.type, folder: options.folder ?? 'produk', name: options.name ?? file.name.replace(/\.[^.]+$/, '') }
    })

    return result.url
}

// ---------------------------------------------------------------------------
// Photo upload with validation, stages and progress (used by the 3D generator)
// ---------------------------------------------------------------------------

export class UploadError extends Error {
    code: string

    constructor(code: string, message: string) {
        super(message)
        this.name = 'UploadError'
        this.code = code
    }
}

export type UploadStage = 'preparing' | 'uploading'

const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
const ACCEPTED_EXTENSIONS = /\.(jpe?g|png|webp)$/i
const MB = 1024 * 1024
const SEND_LIMIT = 4.5 * MB

export const isHeicFile = (file: File) => /image\/hei[cf]/i.test(file.type) || /\.(heic|heif)$/i.test(file.name)

const megabytes = (bytes: number) => `${(bytes / MB).toFixed(1).replace('.', ',')} MB`

// Problems the browser can spot before anything is sent: size and format.
export const checkPhotoFile = (file: File, maxBytes = 10 * MB): string | null => {
    if (file.size > maxBytes) return `Ukuran foto terlalu besar (${megabytes(file.size)}). Maksimal ${Math.round(maxBytes / MB)} MB per foto.`
    if (!file.size) return 'File foto kosong. Pilih foto lain.'
    if (isHeicFile(file)) return null

    const typeOk = ACCEPTED_TYPES.includes(file.type.toLowerCase()) || (!file.type && ACCEPTED_EXTENSIONS.test(file.name))

    return typeOk ? null : 'Format foto tidak didukung. Gunakan JPG, JPEG, PNG, atau WEBP.'
}

const HEIC_MESSAGE = 'Foto HEIC (format bawaan iPhone) belum bisa dibaca browser ini. Ubah dulu ke JPG, misalnya dengan mengatur Kamera iPhone ke format "Paling Kompatibel" atau mengekspor foto sebagai JPG, lalu unggah lagi.'

const failureByStatus = (status: number): string => {
    if (status === 401) return 'Sesi admin berakhir. Masuk kembali di tab baru, lalu tekan Coba lagi.'
    if (status === 403) return 'Izin upload ditolak. Muat ulang halaman ini lalu coba lagi.'
    if (status === 413) return 'Ukuran foto terlalu besar untuk dikirim.'
    if (status >= 500) return 'Server mengalami kesalahan saat menyimpan foto. Coba lagi sebentar.'

    return 'Foto belum bisa diunggah.'
}

const send = (payload: string, onProgress: (percent: number) => void): Promise<string> => new Promise((resolve, reject) => {
    const request = new XMLHttpRequest()

    request.open('POST', '/api/admin/upload')
    request.setRequestHeader('Content-Type', 'application/json')
    request.setRequestHeader('X-EMA-Admin', '1')
    request.timeout = 120000

    request.upload.onprogress = event => {
        if (event.lengthComputable) onProgress(Math.min(99, Math.round((event.loaded / event.total) * 100)))
    }

    request.onload = () => {
        let body: { url?: string, code?: string, message?: string } | null = null

        try {
            body = JSON.parse(request.responseText)
        }
        catch {
            body = null
        }

        if (request.status >= 200 && request.status < 300 && body?.url) {
            onProgress(100)
            resolve(body.url)

            return
        }

        reject(new UploadError(body?.code ?? `http-${request.status}`, body?.message ?? failureByStatus(request.status)))
    }

    request.onerror = () => reject(new UploadError('network', 'Koneksi internet bermasalah. Periksa koneksi, lalu tekan Coba lagi.'))
    request.ontimeout = () => reject(new UploadError('timeout', 'Upload terlalu lama. Periksa koneksi internet, lalu tekan Coba lagi.'))
    request.send(payload)
})

// Checks, compresses (HEIC is converted where the browser can read it) and uploads one photo,
// reporting each stage. Throws UploadError with a reason the admin can act on.
export const uploadPhoto = async (
    file: File,
    options: { folder: 'produk' | 'brand' | 'model-3d', name: string, maxSize?: number, quality?: number, maxBytes?: number },
    onStage: (stage: UploadStage, percent: number) => void
): Promise<string> => {
    const problem = checkPhotoFile(file, options.maxBytes)

    if (problem) throw new UploadError('invalid', problem)

    onStage('preparing', 0)

    let compressed: Awaited<ReturnType<typeof compressImage>>

    try {
        compressed = await compressImage(file, { maxSize: options.maxSize, quality: options.quality })

        // Very detailed photos can stay large; a second, lighter pass keeps them under the limit.
        if (compressed.blob.size > SEND_LIMIT) compressed = await compressImage(file, { maxSize: 1600, quality: 0.8 })
    }
    catch (error) {
        if (isHeicFile(file)) throw new UploadError('heic', HEIC_MESSAGE)

        throw new UploadError('unreadable', error instanceof Error ? error.message : 'Foto tidak bisa dibaca. Gunakan file JPG, PNG, atau WEBP.')
    }

    if (compressed.blob.size > SEND_LIMIT) throw new UploadError('too-large', 'Foto masih terlalu besar setelah dikompres. Coba foto dengan resolusi lebih kecil.')

    const data = await toBase64(compressed.blob)
    const payload = JSON.stringify({ data, contentType: compressed.type, folder: options.folder, name: options.name })

    onStage('uploading', 0)

    return send(payload, percent => onStage('uploading', percent))
}

// ---------------------------------------------------------------------------
// 3D model (GLB) upload
// ---------------------------------------------------------------------------

export type ModelUpload = { url: string, liteUrl: string, meta: ModelMeta }
export type ModelUploadStage = 'uploading' | 'processing'

// Uploads one GLB file as raw bytes. The server checks it, makes a detail and a light version and
// stores both; 'processing' covers that wait after the bytes have arrived.
export const uploadModelFile = async (file: File, name: string, onStage: (stage: ModelUploadStage, percent: number) => void): Promise<ModelUpload> => {
    if (!/\.glb$/i.test(file.name)) throw new UploadError('invalid', 'Format file harus .glb (glTF Binary). File .gltf, .obj, .fbx, atau .stl perlu diekspor ulang sebagai .glb.')
    if (file.size > MAX_MODEL_MB * MB) throw new UploadError('too-large', `File model terlalu besar (${megabytes(file.size)}). Maksimal ${MAX_MODEL_MB} MB.`)
    if (file.size < 20) throw new UploadError('invalid', 'File model kosong atau rusak.')

    const header = new Uint8Array(await file.slice(0, 4).arrayBuffer())

    if (String.fromCharCode(...header) !== 'glTF') throw new UploadError('invalid', 'File ini bukan model GLB yang valid. Ekspor ulang model dalam format .glb.')

    onStage('uploading', 0)

    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest()

        request.open('POST', `/api/admin/models/upload?name=${encodeURIComponent(name.slice(0, 60))}`)
        request.setRequestHeader('Content-Type', 'model/gltf-binary')
        request.setRequestHeader('X-EMA-Admin', '1')
        request.timeout = 10 * 60 * 1000

        request.upload.onprogress = event => {
            if (event.lengthComputable) onStage('uploading', Math.min(99, Math.round((event.loaded / event.total) * 100)))
        }

        request.upload.onload = () => onStage('processing', 100)

        request.onload = () => {
            let body: (Partial<ModelUpload> & { code?: string, message?: string }) | null = null

            try {
                body = JSON.parse(request.responseText)
            }
            catch {
                body = null
            }

            if (request.status >= 200 && request.status < 300 && body?.url && body.liteUrl && body.meta) {
                resolve({ url: body.url, liteUrl: body.liteUrl, meta: body.meta })

                return
            }

            reject(new UploadError(body?.code ?? `http-${request.status}`, body?.message ?? failureByStatus(request.status)))
        }

        request.onerror = () => reject(new UploadError('network', 'Koneksi internet bermasalah. Periksa koneksi, lalu coba unggah lagi.'))
        request.ontimeout = () => reject(new UploadError('timeout', 'Unggahan model terlalu lama. Periksa koneksi internet, lalu coba lagi.'))
        request.send(file)
    })
}

// Asks the server whether it can reach photo storage, so a broken setup shows before any upload.
export const checkUploadStorage = async (): Promise<{ ok: boolean, message?: string }> => {
    try {
        return await adminFetch<{ ok: boolean, message?: string }>('/api/admin/upload')
    }
    catch (error) {
        return { ok: false, message: error instanceof Error ? error.message : 'Pemeriksaan penyimpanan gagal.' }
    }
}

export const discardUpload = (url: string) => {
    adminFetch('/api/admin/upload', { method: 'DELETE', body: { url } }).catch(() => undefined)
}
